import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Package } from 'lucide-react';
import { pushSelectItem } from '../utils/gtm';

const ProductCard = ({ product, listId = null, listName = '', index = 0 }) => {
  const discount = product.discountPercentage ||
    (product.originalPrice && product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0);

  return (
    <Link
      to={`/product/${product.id}`}
      onClick={() => pushSelectItem(product, { listId, listName, index })}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100"
    >
      {/* Image Container */}
      <div className="relative overflow-hidden bg-gray-100" style={{ aspectRatio: '4/5' }}>
        {product.image ? (
          <img
            src={product.image}
            srcSet={product.imageSrcset || undefined}
            sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw"
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
            <Package className="w-12 h-12 mb-2" />
            <span className="text-xs">No image</span>
          </div>
        )}

        {/* Badges */}
        {discount > 0 && (
          <div className="absolute top-2.5 left-2.5">
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
              -{discount}%
            </span>
          </div>
        )}

        {/* Out of Stock Overlay */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-white/90 text-gray-800 text-sm font-semibold px-3 py-1.5 rounded-full">
              Out of Stock
            </span>
          </div>
        )}

      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 p-3.5">
        {/* Category */}
        {product.category && (
          <span className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-1">
            {product.category}
          </span>
        )}

        {/* Name */}
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-2 flex-1">
          {product.name}
        </h3>

        {/* Rating */}
        {product.rating > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-xs font-medium text-gray-700">{product.rating.toFixed(1)}</span>
            {product.reviews > 0 && (
              <span className="text-xs text-gray-400">({product.reviews})</span>
            )}
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2 mt-auto">
          <span className="text-base font-bold text-gray-900">
            ৳{Number(product.price).toLocaleString()}
          </span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="text-xs text-gray-400 line-through">
              ৳{Number(product.originalPrice).toLocaleString()}
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div className="mt-2">
          {product.inStock ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              In Stock
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              Out of Stock
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
